import { Checkbox, NativeSelect, TextInput } from "@mantine/core";

import { Icon } from "./eventPlannerIcons";
import { type EventFilters } from "./eventPlannerUtils";

type EventFiltersBarProps = {
  filters: EventFilters;
  hasConfiguredConnectors: boolean;
  searchPlaceholder: string;
  onChange: (patch: Partial<EventFilters>) => void;
  onReset: () => void;
};

export const PUBLICATION_STATUS_OPTIONS = [
  { value: "all", label: "Tous" },
  { value: "draft", label: "Brouillons" },
  { value: "published", label: "Publiés" },
] as const;

export const CONNECTOR_OPTIONS = [
  { value: "all", label: "Tous" },
  { value: "attention", label: "À revoir" },
  { value: "synced", label: "Synchro" },
  { value: "pending", label: "En attente" },
  { value: "failed", label: "Erreur" },
  { value: "outdated", label: "À mettre à jour" },
  { value: "disabled", label: "Désactivés" },
] as const;

export const FACEBOOK_OPTIONS = [
  { value: "all", label: "Tous" },
  { value: "with_link", label: "Avec lien" },
  { value: "without_link", label: "Sans lien" },
] as const;

export function countActiveFilters(filters: EventFilters) {
  return [
    filters.search.trim().length > 0,
    filters.publication_status !== "all",
    filters.schedule !== "upcoming",
    filters.connector !== "all",
    filters.facebook !== "all",
  ].filter(Boolean).length;
}

export function EventFiltersBar({
  filters,
  hasConfiguredConnectors,
  searchPlaceholder,
  onChange,
  onReset,
}: EventFiltersBarProps) {
  const activeFiltersCount = countActiveFilters(filters);

  return (
    <div className={`event-filter-bar${activeFiltersCount > 0 ? " has-active-filters" : ""}`}>
      <button
        type="button"
        className="event-filter-reset-inline"
        aria-label={activeFiltersCount > 0 ? "Réinitialiser les filtres" : "Aucun filtre actif"}
        title={activeFiltersCount > 0 ? "Réinitialiser les filtres" : "Aucun filtre actif"}
        onClick={onReset}
        disabled={activeFiltersCount === 0}
      >
        <Icon name="x" className="ui-icon" />
      </button>
      <div className={`event-filter-grid${hasConfiguredConnectors ? "" : " is-compact"}`}>
        <TextInput
          type="search"
          label={
            <span className="event-filter-label">
              <Icon name="search" className="ui-icon event-filter-symbol" />
              Recherche
            </span>
          }
          className="event-filter-field event-filter-field-search"
          placeholder={searchPlaceholder}
          value={filters.search}
          leftSection={<Icon name="search" className="ui-icon event-filter-input-icon" />}
          onChange={(event) => onChange({ search: event.currentTarget.value })}
        />

        <NativeSelect
          label={
            <span className="event-filter-label">
              <Icon name="edit" className="ui-icon event-filter-symbol" />
              Statut
            </span>
          }
          className="event-filter-field"
          value={filters.publication_status}
          data={PUBLICATION_STATUS_OPTIONS}
          onChange={(event) =>
            onChange({
              publication_status: event.currentTarget.value as EventFilters["publication_status"],
            })
          }
        />

        <Checkbox
          className="event-filter-field event-filter-checkbox"
          label="Montrer les évènements passés"
          checked={filters.schedule === "all"}
          onChange={(event) => onChange({ schedule: event.currentTarget.checked ? "all" : "upcoming" })}
        />

        {hasConfiguredConnectors ? (
          <NativeSelect
            label={
              <span className="event-filter-label">
                <Icon name="filter" className="ui-icon event-filter-symbol" />
                Connecteurs
              </span>
            }
            className="event-filter-field"
            value={filters.connector}
            data={CONNECTOR_OPTIONS}
            onChange={(event) =>
              onChange({
                connector: event.currentTarget.value as EventFilters["connector"],
              })
            }
          />
        ) : null}

        <NativeSelect
          label={
            <span className="event-filter-label">
              <Icon name="eye" className="ui-icon event-filter-symbol" />
              Facebook
            </span>
          }
          className="event-filter-field"
          value={filters.facebook}
          data={FACEBOOK_OPTIONS}
          onChange={(event) =>
            onChange({
              facebook: event.currentTarget.value as EventFilters["facebook"],
            })
          }
        />
      </div>
    </div>
  );
}
